import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import {
  Button,
  ButtonIcon,
  ButtonText,
  Checkbox,
  Input,
  Modal,
  Pagination,
  SearchInput,
  TextArea,
  useToast,
  Table
} from '@morressier/alembic';
import {
  faDeleteRight,
  faEdit,
  faPlus,
  faTrashCan
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import {
  fetchProfileNotes,
  fetchProfileNotesFilters,
  IProfileNote,
  IProfileNoteAttachment,
  useCreateProfileNote,
  useDeleteProfileNotes,
  useUpdateProfileNote
} from 'src/api/profileNotes';
import { FieldLabel } from 'src/components/Field';
import MultipleFileUpload from 'src/components/File/MultipleFileUpload';
import { IFileUploaded } from 'src/components/FormField/common/types';
import { FormFieldFileUploadArray as IFormFieldFileUploadArray } from '@publishing-engine/domain/types/article';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import SortMenu, {
  SortField
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Sort/SortMenu';
import FilterDrawer from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Filters/FilterDrawer';
import { NOTES_SORT_OPTIONS } from 'src/constants';
import FileViewerButtonText from '../../FileViewerButtonText';

const LIMIT = 10;

const EMPTY_NOTE_FILTERS: Record<string, string[]> = {
  journalId: [],
  createdBy: [],
  updatedBy: []
};

const formatFilterUserName = (user: {
  firstName?: string;
  lastName?: string;
  id: string;
}) => {
  const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return fullName || user.id;
};

type NoteFormValues = {
  title: string;
  content: string;
  attachments: IFileUploaded[];
};

type NoteModalState =
  | { isOpen: false; mode: null; note: null }
  | { isOpen: true; mode: 'add'; note: null }
  | { isOpen: true; mode: 'edit'; note: IProfileNote };

const attachmentConfig: Omit<IFormFieldFileUploadArray, 'component'> = {
  name: 'attachments',
  caseDataFields: {
    main: 'attachments'
  },
  params: {
    validations: {
      kind: 'fileArray',
      maxSizeMB: 25,
      maxItems: 15,
      allowedFileFormats: {
        'image/jpeg': ['.jpg'],
        'image/png': ['.png'],
        'application/pdf': ['.pdf'],
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          ['.docx'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
          '.xlsx'
        ]
      },
      required: false
    },
    labels: {
      main: 'Attach File(s)',
      subtitle: ''
    }
  }
};

const mapApiAttachmentsToUpload = (
  attachments?: IProfileNoteAttachment[]
): IFileUploaded[] =>
  (attachments ?? []).map(attachment => ({
    fileId: attachment.referenceName ?? attachment.name,
    url: attachment.url,
    name: attachment.name,
    referenceName: attachment.referenceName ?? attachment.name,
    isUploading: false,
    progress: 100,
    extension: attachment.extension ?? attachment.name.split('.').pop() ?? ''
  }));

const getAttachmentPayload = (attachments: IFileUploaded[] = []) =>
  attachments
    .filter(attachment => !attachment.isUploading)
    .map(attachment => ({
      referenceName: attachment.referenceName || attachment.name,
      name: attachment.name,
      extension: attachment.extension,
      url: attachment.url
    }));

const formatDate = (date?: string) => {
  if (!date) return '-';

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '-';

  return format(parsed, 'MMM dd, yyyy');
};

const getCreatedAt = (note: IProfileNote) => formatDate(note.created_at);

const getUpdatedAt = (note: IProfileNote) => formatDate(note.modified_at);

const NoteDetailsModal = ({
  isOpen,
  onClose,
  note,
  organizationId,
  canManage,
  onEdit,
  onDelete
}: {
  isOpen: boolean;
  onClose: () => void;
  note: IProfileNote | null;
  organizationId?: string;
  canManage: boolean;
  onEdit: (note: IProfileNote) => void;
  onDelete: (note: IProfileNote) => void;
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="700px"
      closeButtonProps={{ bg: 'white' }}
    >
      <Flex direction="column" maxH="80vh">
        <Flex flexDirection="column" padding={4} flexShrink={0}>
          <Text textStyle="headingGroup" tabIndex={1}>
            View Note
          </Text>
        </Flex>
        <Box overflowY="auto" p={4} flex={1}>
          <Flex flexDirection="column" gap={4}>
            {note?.title && (
              <Flex flexDirection="column">
                <Text textStyle="headingItem">Title</Text>
                <Text textStyle="textBody">{note.title}</Text>
              </Flex>
            )}
            {note?.content && (
              <Flex flexDirection="column">
                <Text textStyle="headingItem">Note</Text>
                <Text textStyle="textBody" whiteSpace="pre-wrap">
                  {note.content}
                </Text>
              </Flex>
            )}
            <Flex flexDirection="column" gap={2}>
              <Flex flexDirection="column">
                <Text textStyle="headingItem">Created On</Text>
                <Text textStyle="textBody">
                  {note ? getCreatedAt(note) : '-'}
                </Text>
                <Text textStyle={'textBody'}>
                  {note
                    ? note.isSystemGenerated
                      ? 'by System'
                      : `by ${note.createdByUser?.firstName} ${note.createdByUser?.lastName}`
                    : '-'}
                </Text>
              </Flex>

              <Flex flexDirection="column">
                <Text textStyle="headingItem">Last Edited On</Text>
                <Text textStyle="textBody">
                  {note ? getUpdatedAt(note) : '-'}
                </Text>
                <Text textStyle={'textBody'}>
                  {note
                    ? note.isSystemGenerated
                      ? 'by System'
                      : `by ${note.updatedByUser?.firstName} ${note.updatedByUser?.lastName}`
                    : '-'}
                </Text>
              </Flex>
            </Flex>
            {note?.attachments && note.attachments.length > 0 && (
              <Flex flexDirection="column" gap={0}>
                <Text textStyle="headingItem">Files</Text>
                {note.attachments.map((file, index) => (
                  <Flex key={index} alignItems="center">
                    <FileViewerButtonText
                      file={file}
                      textStyle="textBody"
                      flexShrink={0}
                      submissionContextId={note?.submissionContextId}
                      organizationId={note?.organizationId ?? organizationId}
                    >
                      {file?.name}
                    </FileViewerButtonText>
                  </Flex>
                ))}
              </Flex>
            )}
            {canManage && (
              <Flex
                gap={2}
                justifyContent="flex-end"
                alignItems="center"
                alignSelf="stretch"
                mt={4}
              >
                <Button
                  variant="caution"
                  onClick={() => note && onDelete(note)}
                  borderRadius="small"
                >
                  <FontAwesomeIcon icon={faTrashCan} cursor="pointer" />
                  Delete
                </Button>
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => note && onEdit(note)}
                  borderRadius="small"
                >
                  <FontAwesomeIcon icon={faEdit} cursor="pointer" />
                  Edit
                </Button>
              </Flex>
            )}
          </Flex>
        </Box>
      </Flex>
    </Modal>
  );
};

const NoteEditorModal = ({
  isOpen,
  onClose,
  mode,
  note,
  onSave,
  isSaving,
  organizationId
}: {
  isOpen: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  note?: IProfileNote | null;
  onSave: (values: NoteFormValues) => Promise<void>;
  isSaving: boolean;
  organizationId?: string;
}) => {
  const toast = useToast();
  const methods = useForm<NoteFormValues>({
    defaultValues: {
      title: '',
      content: '',
      attachments: []
    }
  });
  const {
    handleSubmit,
    register,
    reset,
    getValues,
    formState: { errors },
    watch
  } = methods;
  const attachments = watch('attachments') ?? [];
  const hasUploadingAttachments = attachments.some(
    attachment => attachment.isUploading
  );

  useEffect(() => {
    if (!isOpen) return;

    reset({
      title: note?.title ?? '',
      content: note?.content ?? '',
      attachments: mapApiAttachmentsToUpload(note?.attachments)
    });
  }, [isOpen, note, reset]);

  const onSubmit = async (values: NoteFormValues) => {
    try {
      const attachments = values.attachments ?? [];
      const pendingUploads = getValues('attachments')?.some(
        attachment => attachment.isUploading
      );

      if (pendingUploads) {
        toast({
          variant: 'attention',
          title: 'Please wait for file uploads to finish'
        });
        return;
      }

      await onSave({
        ...values,
        attachments
      });
      reset({
        title: '',
        content: '',
        attachments: []
      });
      onClose();
    } catch {
      toast({
        variant: 'error',
        title: mode === 'add' ? 'Failed to add note' : 'Failed to update note'
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="720px">
      <Flex direction="column" px={4} pt={4} pb={2} gap={2}>
        <Text textStyle="headingGroup">
          {mode === 'add' ? 'Add Note' : 'Edit Note'}
        </Text>
      </Flex>

      <Box px={4} pb={4}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex direction="column" gap={4}>
              <Flex direction="column" gap={2}>
                <FieldLabel label="Title" isRequired={false} subText="" />
                <Input
                  {...register('title', {
                    maxLength: {
                      value: 100,
                      message: 'Title cannot exceed 100 characters'
                    }
                  })}
                  placeholder="Enter a title"
                  maxLength={100}
                />
                <Flex justifyContent="space-between" gap={2}>
                  <Text textStyle="textContext">
                    Character limit for the title is 100 characters.
                  </Text>
                  <Text textStyle="textContext">
                    {(watch('title') || '').length} / 100
                  </Text>
                </Flex>
                {errors.title && (
                  <Text textStyle="textContext" color="red.500">
                    {errors.title.message}
                  </Text>
                )}
              </Flex>

              <Flex direction="column" gap={2}>
                <FieldLabel label="Note" isRequired={true} subText="" />
                <TextArea
                  value={watch('content') || ''}
                  {...register('content', {
                    required: 'Note is required',
                    maxLength: {
                      value: 1000,
                      message: 'Note cannot exceed 1000 characters'
                    }
                  })}
                  minHeight={160}
                  placeholder="Write a note"
                  maxLength={1000}
                />
                <Flex justifyContent="space-between" gap={2}>
                  <Text textStyle="textContext">
                    Character limit for note is 1000 characters.
                  </Text>
                  <Text textStyle="textContext">
                    {(watch('content') || '').length} / 1000
                  </Text>
                </Flex>
                {errors.content && (
                  <Text textStyle="textContext" color="red.500">
                    {errors.content.message}
                  </Text>
                )}
              </Flex>

              {organizationId ? (
                <MultipleFileUpload
                  organizationId={organizationId}
                  config={attachmentConfig}
                />
              ) : (
                <Text textStyle="textContext" color="gray.600">
                  Attachments are available when an organization is selected.
                </Text>
              )}

              <Flex justifyContent="flex-end" gap={3} pt={2}>
                <Button
                  variant="secondary"
                  onClick={onClose}
                  type="button"
                  disabled={isSaving || hasUploadingAttachments}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  isLoading={isSaving}
                  disabled={isSaving || hasUploadingAttachments}
                >
                  {mode === 'add' ? 'Add Note' : 'Save Changes'}
                </Button>
              </Flex>
            </Flex>
          </form>
        </FormProvider>
      </Box>
    </Modal>
  );
};

export default function ProfileNotesSection({
  userId,
  submissionContextId,
  organizationId,
  onTotalChange,
  canEditNotes = true
}: {
  userId?: string;
  submissionContextId?: string;
  organizationId?: string;
  onTotalChange?: (total: number) => void;
  canEditNotes?: boolean;
}) {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [typedSearch, setTypedSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [sort, setSort] = useState<SortField | null>(null);
  const [selectedNoteIds, setSelectedNoteIds] = useState<string[]>([]);
  const [selectedNote, setSelectedNote] = useState<IProfileNote | null>(null);
  const [selectedFilters, setSelectedFilters] =
    useState<Record<string, string[]>>(EMPTY_NOTE_FILTERS);
  const [modalState, setModalState] = useState<NoteModalState>({
    isOpen: false,
    mode: null,
    note: null
  });
  const [deleteState, setDeleteState] = useState<{
    isOpen: boolean;
    noteIds: string[];
  }>({
    isOpen: false,
    noteIds: []
  });

  const { watch } = useForm({
    defaultValues: EMPTY_NOTE_FILTERS
  });

  const queryKey = useMemo(
    () => ['profile-notes', { userId, submissionContextId }],
    [submissionContextId, userId]
  );

  const selectedJournalIds = selectedFilters.journalId ?? [];
  const selectedCreatedByIds = selectedFilters.createdBy ?? [];
  const selectedUpdatedByIds = selectedFilters.updatedBy ?? [];
  const canManageNotes = Boolean(submissionContextId) && canEditNotes;

  // Empty journalId → backend visibility (CoC + global). Selected → strict match.
  const filters = useMemo(
    () => ({
      searchParams: searchValue.trim() || '',
      journalId: selectedJournalIds,
      createdBy: selectedCreatedByIds,
      updatedBy: selectedUpdatedByIds
    }),
    [
      searchValue,
      selectedCreatedByIds,
      selectedJournalIds,
      selectedUpdatedByIds
    ]
  );

  const hasFilters =
    selectedJournalIds.length > 0 ||
    selectedCreatedByIds.length > 0 ||
    selectedUpdatedByIds.length > 0;

  const { data: filterOptions } = useQuery({
    queryKey: ['profile-notes-filters', { userId }],
    queryFn: () => fetchProfileNotesFilters({ userId: userId! }),
    enabled: Boolean(userId)
  });

  const filterGroups = useMemo(
    () => [
      {
        key: 'journalId',
        label: 'Journals',
        items: (filterOptions?.journals ?? []).map(journal => ({
          value: journal.id,
          name: journal.shortname || journal.name
        }))
      },
      {
        key: 'createdBy',
        label: 'Created By',
        items: (filterOptions?.createdBy ?? []).map(user => ({
          value: user.id,
          name: formatFilterUserName(user)
        }))
      },
      {
        key: 'updatedBy',
        label: 'Updated By',
        items: (filterOptions?.updatedBy ?? []).map(user => ({
          value: user.id,
          name: formatFilterUserName(user)
        }))
      }
    ],
    [
      filterOptions?.createdBy,
      filterOptions?.journals,
      filterOptions?.updatedBy
    ]
  );

  const { data, isLoading } = useQuery({
    queryKey: [...queryKey, { page, filters, sort }],
    queryFn: () =>
      userId
        ? fetchProfileNotes({
            userId,
            offset: (page - 1) * LIMIT,
            limit: LIMIT,
            filters,
            sort: sort ?? {
              value: 'created_at',
              label: '',
              direction: 'desc'
            }
          })
        : null,
    enabled: Boolean(userId)
  });

  useEffect(() => {
    onTotalChange?.(data?.total ?? 0);
  }, [data?.total, onTotalChange]);

  const createMutation = useCreateProfileNote({
    userId: userId ?? '',
    queryKey
  });
  const updateMutation = useUpdateProfileNote({
    userId: userId ?? '',
    queryKey
  });
  const deleteMutation = useDeleteProfileNotes({
    userId: userId ?? '',
    queryKey
  });

  const totalPages = data?.total ? Math.ceil(data.total / LIMIT) : 0;
  const offset = (page - 1) * LIMIT;
  const notes = data?.items ?? [];
  const currentNoteIds = notes.map(note => note.id);
  const isDeleteDisabled = selectedNoteIds.length === 0;
  const allCurrentSelected =
    currentNoteIds.length > 0 &&
    currentNoteIds.every(id => selectedNoteIds.includes(id));
  const someCurrentSelected =
    currentNoteIds.some(id => selectedNoteIds.includes(id)) &&
    !allCurrentSelected;

  useEffect(() => {
    if (page > 1 && notes.length === 0 && data?.total) {
      setPage(Math.max(1, Math.ceil(data.total / LIMIT)));
    }
  }, [data?.total, notes.length, page]);

  useEffect(() => {
    setSelectedFilters(EMPTY_NOTE_FILTERS);
    setPage(1);
  }, [submissionContextId]);

  useEffect(() => {
    setPage(1);
  }, [selectedJournalIds, selectedCreatedByIds, selectedUpdatedByIds]);

  useEffect(() => {
    setSelectedNoteIds([]);
  }, [
    page,
    searchValue,
    submissionContextId,
    selectedJournalIds,
    selectedCreatedByIds,
    selectedUpdatedByIds
  ]);

  const applySearch = () => {
    setSearchValue(typedSearch.trim());
    setPage(1);
    setSelectedNoteIds([]);
  };

  const clearSearch = () => {
    setTypedSearch('');
    setSearchValue('');
    setPage(1);
    setSelectedNoteIds([]);
  };

  const handleFiltersSubmit = (nextFilters: Record<string, string[]>) => {
    setSelectedFilters({
      journalId: nextFilters.journalId ?? [],
      createdBy: nextFilters.createdBy ?? [],
      updatedBy: nextFilters.updatedBy ?? []
    });
    setPage(1);
    setSelectedNoteIds([]);
  };

  const clearFilters = () => {
    setSelectedFilters(EMPTY_NOTE_FILTERS);
    setPage(1);
    setSelectedNoteIds([]);
  };

  const handleSortApply = (selected: SortField | null) => {
    setSort(selected);
    setPage(1);
    setSelectedNoteIds([]);
  };

  const handleSortReset = () => {
    setSort(null);
    setPage(1);
    setSelectedNoteIds([]);
  };

  const toggleNoteSelection = (noteId: string, isChecked: boolean) => {
    setSelectedNoteIds(prev =>
      isChecked ? [...prev, noteId] : prev.filter(id => id !== noteId)
    );
  };

  const toggleSelectAll = (isChecked: boolean) => {
    setSelectedNoteIds(prev => {
      const currentSet = new Set(prev);
      if (isChecked) {
        currentNoteIds.forEach(id => currentSet.add(id));
      } else {
        currentNoteIds.forEach(id => currentSet.delete(id));
      }
      return [...currentSet];
    });
  };

  const handleSave = async (values: NoteFormValues) => {
    if (!userId) {
      throw new Error('Missing user id');
    }

    const title = values.title.trim();
    const content = values.content.trim();
    const attachments = getAttachmentPayload(values.attachments);

    if (modalState.mode === 'add') {
      const response = await createMutation.mutateAsync({
        title,
        content,
        ...(submissionContextId ? { submissionContextId } : {}),
        attachments
      });

      if (!response.isSuccess) {
        throw new Error(response.message);
      }

      toast({
        variant: 'success',
        title: 'Note added'
      });
      return;
    }

    if (modalState.mode === 'edit' && modalState.note) {
      const updatePayload: {
        title?: string;
        content?: string;
        attachments?: IProfileNoteAttachment[];
      } = {};

      if (title !== modalState.note.title) {
        updatePayload.title = title;
      }

      if (content !== modalState.note.content) {
        updatePayload.content = content;
      }

      updatePayload.attachments = attachments;

      const response = await updateMutation.mutateAsync({
        noteId: modalState.note.id,
        payload: updatePayload
      });

      if (!response.isSuccess) {
        throw new Error(response.message);
      }

      toast({
        variant: 'success',
        title: 'Note updated'
      });
    }
  };

  const openDeleteDialog = (noteIds: string[]) => {
    setDeleteState({
      isOpen: true,
      noteIds
    });
  };

  const openNoteDetails = (note: IProfileNote) => {
    setSelectedNote(note);
  };

  const closeNoteDetails = () => {
    setSelectedNote(null);
  };

  const handleDelete = async () => {
    if (!deleteState.noteIds.length) return;

    const response = await deleteMutation.mutateAsync(deleteState.noteIds);
    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: deleteState.noteIds.length > 1 ? 'Notes deleted' : 'Note deleted'
      });
      setDeleteState({
        isOpen: false,
        noteIds: []
      });
      setSelectedNoteIds(prev =>
        prev.filter(id => !deleteState.noteIds.includes(id))
      );
      return;
    }

    toast({
      variant: 'error',
      title: response.message
    });
  };

  if (!userId) return null;

  return (
    <Flex direction="column" gap={3} width="100%">
      <Flex alignItems="flex-start" justifyContent="space-between" gap={3}>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          gap={3}
          flex="0.8"
        >
          <SearchInput
            placeholder="Search notes by title & content"
            iconLeft={<FontAwesomeIcon icon={faSearch} />}
            width="100%"
            name="searchParams"
            value={typedSearch}
            enterKeyHint="search"
            onChange={e => setTypedSearch(e.currentTarget.value)}
            onInputClear={clearSearch}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applySearch();
              }
            }}
            onKeyUp={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applySearch();
              }
            }}
          />

          <Flex justifyContent="flex-end" gap={2}>
            <FilterDrawer
              filterGroups={filterGroups}
              watch={watch as never}
              onSubmit={handleFiltersSubmit}
              selectedFilters={selectedFilters}
              setSelectedFilters={handleFiltersSubmit}
            />
            <SortMenu
              appliedSort={sort}
              options={{ fields: NOTES_SORT_OPTIONS }}
              onApply={handleSortApply}
              onReset={handleSortReset}
            />
          </Flex>
        </Flex>
        {canManageNotes && (
          <Flex gap={2} alignItems="center" flexWrap="wrap">
            <Button
              leftIcon={<FontAwesomeIcon icon={faTrashCan} />}
              variant="caution"
              disabled={isDeleteDisabled}
              onClick={() => {
                if (isDeleteDisabled) return;
                openDeleteDialog(selectedNoteIds);
              }}
            >
              Delete
            </Button>
            <Button
              leftIcon={<FontAwesomeIcon icon={faPlus} />}
              variant="primary"
              onClick={() =>
                setModalState({
                  isOpen: true,
                  mode: 'add',
                  note: null
                })
              }
            >
              Add Note
            </Button>
          </Flex>
        )}
      </Flex>
      <Flex alignItems="center" gap={2} flexWrap="wrap">
        <Text textStyle="textContext">
          {notes.length > 0 &&
            `Showing ${offset + 1} - ${Math.min(
              offset + notes.length,
              data?.total ?? 0
            )} of ${data?.total ?? 0} notes`}
        </Text>
        {hasFilters && (
          <ButtonText
            onClick={clearFilters}
            leftIcon={<FontAwesomeIcon icon={faDeleteRight} />}
            variant="secondary"
          >
            Clear filters
          </ButtonText>
        )}
      </Flex>
      {isLoading ? (
        <Flex justifyContent="center" py={6}>
          <Spinner />
        </Flex>
      ) : notes.length ? (
        <>
          <Box
            width="100%"
            overflowX="auto"
            borderWidth="1px"
            borderRadius="md"
          >
            <Table>
              <Table.HeaderRow p={4}>
                {canManageNotes && (
                  <Table.HeaderCell flex="0.1" textAlign="left" mr={4}>
                    <Checkbox
                      ariaLabel="Select all notes"
                      id="select-all-notes"
                      name="select-all-notes"
                      isChecked={allCurrentSelected}
                      isIndeterminate={someCurrentSelected}
                      onChange={e => toggleSelectAll(e.target.checked)}
                    >
                      {''}
                    </Checkbox>
                  </Table.HeaderCell>
                )}
                <Table.HeaderCell flex="1" textAlign="left">
                  Title &amp; Note
                </Table.HeaderCell>
                <Table.HeaderCell flex="1" textAlign="left">
                  Journal
                </Table.HeaderCell>
                <Table.HeaderCell flex="1" textAlign="left">
                  Created On
                </Table.HeaderCell>
                <Table.HeaderCell flex="1" textAlign="left">
                  Last Edited On
                </Table.HeaderCell>
                {canManageNotes && (
                  <Table.HeaderCell textAlign="right">Actions</Table.HeaderCell>
                )}
              </Table.HeaderRow>
              <Table.Body>
                {notes.map(note => (
                  <Table.Row key={note.id} p={4}>
                    {canManageNotes && (
                      <Table.RowCell flex="0.1" textAlign="left" mr={4}>
                        <Checkbox
                          ariaLabel={`Select note ${note.title}`}
                          id={`select-note-${note.id}`}
                          name={`select-note-${note.id}`}
                          isChecked={selectedNoteIds.includes(note.id)}
                          onChange={e =>
                            toggleNoteSelection(note.id, e.target.checked)
                          }
                        >
                          {''}
                        </Checkbox>
                      </Table.RowCell>
                    )}
                    <Table.RowCell flex="1" textAlign="left">
                      <Flex direction="column" gap={1}>
                        <Box
                          onClick={() => openNoteDetails(note)}
                          cursor="pointer"
                        >
                          <Text fontWeight="600" color="#2043DF" noOfLines={1}>
                            {note.title ? note.title : note.content}
                          </Text>
                        </Box>
                        {note.attachments && note.attachments.length > 0 && (
                          <Text textStyle="textContext" color="gray.600">
                            {note.attachments.length} file
                            {note.attachments.length > 1 ? 's' : ''}
                          </Text>
                        )}
                      </Flex>
                    </Table.RowCell>
                    <Table.RowCell flex="1">
                      <Text textStyle="textBody">
                        {note.journal?.shortname || note.journal?.name || '-'}
                      </Text>
                    </Table.RowCell>
                    <Table.RowCell flex="1" textAlign="left">
                      <Text textStyle="textBody">{getCreatedAt(note)}</Text>
                      {note?.isSystemGenerated ? (
                        <Text textStyle="textBody">by System</Text>
                      ) : note?.createdByUser ? (
                        <Text textStyle="textBody">
                          by {note?.createdByUser?.firstName}{' '}
                          {note?.createdByUser?.lastName}
                        </Text>
                      ) : null}
                    </Table.RowCell>
                    <Table.RowCell flex="1" textAlign="left">
                      <Text textStyle="textBody">{getUpdatedAt(note)}</Text>
                      {note?.isSystemGenerated ? (
                        <Text textStyle="textBody">by System</Text>
                      ) : note?.updatedByUser ? (
                        <Text textStyle="textBody">
                          by {note?.updatedByUser?.firstName}{' '}
                          {note?.updatedByUser?.lastName}
                        </Text>
                      ) : null}
                    </Table.RowCell>
                    {canManageNotes && !note?.isSystemGenerated && (
                      <Table.RowCell>
                        <Flex justifyContent="flex-end" gap={2}>
                          <ButtonIcon
                            size="small"
                            ariaLabel="Edit note"
                            icon={<FontAwesomeIcon icon={faEdit} />}
                            onClick={() =>
                              setModalState({
                                isOpen: true,
                                mode: 'edit',
                                note
                              })
                            }
                          />
                          <ButtonIcon
                            size="small"
                            ariaLabel="Delete note"
                            icon={<FontAwesomeIcon icon={faTrashCan} />}
                            onClick={() => openDeleteDialog([note.id])}
                          />
                        </Flex>
                      </Table.RowCell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Box>

          {totalPages > 1 && (
            <Flex width="100%" justifyContent="center" pt={2}>
              <Pagination
                ariaLabel="Profile notes pagination"
                currentPage={page}
                onPageChange={setPage}
                totalPages={totalPages}
              />
            </Flex>
          )}
        </>
      ) : (
        <Flex
          direction="column"
          gap={3}
          alignItems="flex-start"
          bg="white"
          borderWidth="1px"
          borderRadius="md"
          p={4}
        >
          <Text textStyle="textBody" color="gray.600">
            No notes yet.
          </Text>
        </Flex>
      )}

      <NoteEditorModal
        isOpen={modalState.isOpen}
        onClose={() =>
          setModalState({
            isOpen: false,
            mode: null,
            note: null
          })
        }
        mode={modalState.mode ?? 'add'}
        note={modalState.note}
        onSave={handleSave}
        isSaving={createMutation.isLoading || updateMutation.isLoading}
        organizationId={organizationId}
      />

      <NoteDetailsModal
        isOpen={!!selectedNote}
        onClose={closeNoteDetails}
        note={selectedNote}
        organizationId={organizationId}
        canManage={canManageNotes}
        onDelete={note => {
          closeNoteDetails();
          openDeleteDialog([note.id]);
        }}
        onEdit={note => {
          closeNoteDetails();
          setModalState({
            isOpen: true,
            mode: 'edit',
            note
          });
        }}
      />

      <Modal
        isOpen={deleteState.isOpen}
        onClose={() =>
          setDeleteState({
            isOpen: false,
            noteIds: []
          })
        }
        width="520px"
      >
        <Box p={4}>
          <Text textStyle="headingGroup">
            Delete {deleteState.noteIds.length}{' '}
            {deleteState.noteIds.length > 1 ? 'Notes' : 'Note'}?
          </Text>
          <Text mt={2} textStyle="textBody" color="gray.600">
            This action is irreversible.
          </Text>

          <Flex justifyContent="flex-end" gap={3} mt={6}>
            <Button
              variant="secondary"
              onClick={() =>
                setDeleteState({
                  isOpen: false,
                  noteIds: []
                })
              }
            >
              Cancel
            </Button>
            <Button
              variant="caution"
              onClick={handleDelete}
              isLoading={deleteMutation.isLoading}
            >
              Delete
            </Button>
          </Flex>
        </Box>
      </Modal>
    </Flex>
  );
}
